import { PfApiError, request } from "./pfConfigApi";

// PF Establishment Codes (P2) and Exit Codes (P3). The screens keep the
// sample-data shape ("Active", site / reason names for display) and work
// with ids; these mappers convert to and from the API shape ("ACTIVE", ids).

const CODE_STATUS = { ACTIVE: "Active", INACTIVE: "Inactive" };
const MAPPING_STATUS = { MAPPED: "Mapped", NOT_MAPPED: "Not mapped" };

const text = (value) => (value == null ? "" : String(value));
const payload = (data) => {
  if (!data || typeof data !== "object" || !data.data) throw new PfApiError("The server returned an invalid response.");
  return data.data;
};

// ---- Establishment codes ----

export const fromApiCode = (api) => ({
  id: api.id,
  code: text(api.code),
  type: api.type,
  branch_name: text(api.branch_name),
  coverage_from: text(api.coverage_from),
  epfo_office_id: api.epfo_office?.id ?? "",
  epfo_office: text(api.epfo_office?.name),
  address: text(api.address),
  portal_login_owner: text(api.portal_login_owner),
  status: CODE_STATUS[api.status] || text(api.status),
  members: Number(api.members) || 0,
  row_version: api.row_version ?? null,
  site_ids: (api.sites || []).map((site) => site.id),
  sites: (api.sites || []).map((site) => text(site.name)),
});

export const fromApiCodes = (data) => ({
  codes: (data.codes || []).map(fromApiCode),
  // Every site (Administration > Sites) for the drawer's "Sites covered"
  // table: this company's employees there and the active code covering it.
  sites: (data.sites || []).map((site) => ({
    id: site.id,
    name: text(site.name),
    code: text(site.code),
    is_active: site.is_active !== false,
    employees: Number(site.employees) || 0,
    linked_code_id: site.linked_code_id ?? null,
    linked_code: site.linked_code ?? null,
  })),
  unlinked: (data.unlinked_sites || []).map((site) => ({
    id: site.id,
    name: text(site.name),
    code: text(site.code),
    employees: Number(site.employees) || 0,
  })),
});

// The POST / PUT body. Never sends members or site names.
export const toApiCode = (form, companyId) => ({
  company_id: Number(companyId),
  code: text(form.code).trim().toUpperCase(),
  type: form.type,
  branch_name: text(form.branch_name).trim(),
  coverage_from: form.coverage_from || null,
  epfo_office_id: form.epfo_office_id === "" || form.epfo_office_id == null ? null : Number(form.epfo_office_id),
  // Typed office (not in the EPFO list): sent by name, stored on the code.
  epfo_office_name: form.epfo_office_id === "" || form.epfo_office_id == null ? text(form.epfo_office).trim() : "",
  address: text(form.address),
  portal_login_owner: text(form.portal_login_owner),
  status: text(form.status).toUpperCase(),
  site_ids: form.site_ids || [],
});

export const fetchEstablishmentCodes = async ({ domain, companyId, signal }) =>
  fromApiCodes(payload(await request({ domain, path: "establishment-codes/", query: { company_id: companyId }, signal })));

// Add (no id) or update a code. Returns { code, message }.
export const saveEstablishmentCode = async ({ domain, companyId, form }) => {
  const editing = form.id != null;
  const data = await request({
    domain,
    path: editing ? `establishment-codes/${form.id}/` : "establishment-codes/",
    method: editing ? "PUT" : "POST",
    body: editing ? { ...toApiCode(form, companyId), row_version: form.row_version } : toApiCode(form, companyId),
  });
  return { code: fromApiCode(payload(data)), message: data.message };
};

// Uploaded manually by Cauver ops - can be [].
export const fetchEpfoOffices = async ({ domain, signal }) =>
  (payload(await request({ domain, path: "epfo-offices/", signal })) || []).map((office) => ({
    id: office.id,
    name: text(office.name),
    state: text(office.state),
  }));

// ---- Exit codes ----

const reasonRef = (reason) => ({ id: reason.id, name: text(reason.name) });

export const fromApiExitCodes = (data) => ({
  codes: (data.codes || []).map((code) => ({
    code: code.code,
    label: text(code.label),
    reasons: (code.reasons || []).map(reasonRef),
    status: MAPPING_STATUS[code.status] || text(code.status),
    recent: code.recent_usage ?? null,
  })),
  reasons: (data.exit_reasons || []).map((reason) => ({ ...reasonRef(reason), mapped_code: reason.mapped_code ?? null })),
  unmapped: (data.unmapped_reasons || []).map(reasonRef),
});

export const fetchExitCodes = async ({ domain, companyId, signal }) =>
  fromApiExitCodes(payload(await request({ domain, path: "exit-codes/", query: { company_id: companyId }, signal })));

// Saves the code's whole reason list; returns the whole tab data + message.
export const saveExitMapping = async ({ domain, companyId, code, reasonIds }) => {
  const data = await request({
    domain,
    path: `exit-codes/${code}/mapping/`,
    method: "PUT",
    body: { company_id: Number(companyId), exit_reason_ids: reasonIds },
  });
  return { ...fromApiExitCodes(payload(data)), message: data.message };
};
